import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
  type ServiceApi,
  adminFetchServices,
  adminSaveService,
  adminDeleteService,
  adminUploadServiceImage,
} from '@/api/client';
import { services as mockServices } from '@/mocks/services';

function normalizeActive(val: number | boolean): boolean {
  return val === 1 || val === true;
}

const iconOptions = [
  { value: 'ri-home-5-line', label: 'Home' },
  { value: 'ri-building-2-line', label: 'Building' },
  { value: 'ri-briefcase-4-line', label: 'Briefcase' },
  { value: 'ri-brush-line', label: 'Brush' },
  { value: 'ri-truck-line', label: 'Truck' },
  { value: 'ri-hammer-line', label: 'Hammer' },
  { value: 'ri-calendar-check-line', label: 'Calendar' },
  { value: 'ri-cake-line', label: 'Event' },
  { value: 'ri-sparkling-line', label: 'Sparkle' },
  { value: 'ri-star-line', label: 'Star' },
];

function mockToServiceApi(m: typeof mockServices[number], index: number): ServiceApi {
  return {
    id: -(index + 1),
    title: m.title,
    short_description: m.description,
    full_description: m.description,
    icon: m.icon,
    image_url: m.image,
    sort_order: index + 1,
    active: 1,
  };
}

export default function AdminServicesManager() {
  const { t } = useTranslation('home');
  const [items, setItems] = useState<ServiceApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [usingMock, setUsingMock] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<ServiceApi>({
    id: 0,
    title: '',
    short_description: '',
    full_description: '',
    icon: '',
    sort_order: 1,
    active: 1,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadServices = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    setUsingMock(false);
    try {
      const { data, error } = await adminFetchServices();
      if (error) {
        setItems(mockServices.map(mockToServiceApi));
        setFetchError(error);
        setUsingMock(true);
      } else if (data.length === 0) {
        // Backend reachable but empty — preload defaults so admin can edit/save
        setItems(mockServices.map(mockToServiceApi));
        setUsingMock(true);
      } else {
        setItems(data);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setItems(mockServices.map(mockToServiceApi));
      setFetchError(msg);
      setUsingMock(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const seedDefaultServices = useCallback(async () => {
    setSeeding(true);
    for (let i = 0; i < mockServices.length; i++) {
      const m = mockServices[i];
      await adminSaveService({
        title: m.title,
        short_description: m.description,
        full_description: m.description,
        icon: m.icon,
        image_url: m.image,
        sort_order: i + 1,
        active: 1,
      });
    }
    setSeeding(false);
    // Reload to show newly seeded services
    await loadServices();
  }, [loadServices]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const handleEdit = (index: number) => {
    setEditing(index);
    setEditForm({ ...items[index] });
  };

  const handleAdd = () => {
    setEditing(-1);
    setEditForm({
      id: 0,
      title: '',
      short_description: '',
      full_description: '',
      icon: iconOptions[0].value,
      sort_order: items.length + 1,
      active: 1,
    });
  };

  const saveEdit = async () => {
    // If editing a mock item (negative id), strip the id so the backend creates a new record
    const payload = {
      ...editForm,
      id: editForm.id > 0 ? editForm.id : undefined,
      sort_order: Number(editForm.sort_order) || 1,
      active: editForm.active === true || editForm.active === 1 ? 1 : 0,
    };
    setLoading(true);
    const res = await adminSaveService(payload);
    setLoading(false);
    if (res.success && res.data) {
      Swal.fire({
        title: t('admin_api_saved'),
        icon: 'success',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
        timer: 2000,
        timerProgressBar: true,
      });
      await loadServices();
      setEditing(null);
    } else {
      Swal.fire({
        title: t('admin_api_error'),
        text: res.message || '',
        icon: 'error',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
    }
  };

  const handleDelete = (index: number) => {
    const item = items[index];
    Swal.fire({
      title: `${t('admin_delete')} ${t('admin_service')}?`,
      text: t('admin_delete_confirm'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#1A2E4A',
      confirmButtonText: t('admin_delete'),
      cancelButtonText: t('admin_cancel'),
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        const res = await adminDeleteService(item.id);
        setLoading(false);
        if (res.success) {
          Swal.fire({
            title: t('admin_api_deleted'),
            icon: 'success',
            confirmButtonColor: '#3DBFA0',
            timer: 1500,
          });
          await loadServices();
        } else {
          Swal.fire({
            title: t('admin_api_error'),
            text: res.message || '',
            icon: 'error',
            confirmButtonColor: '#3DBFA0',
            confirmButtonText: t('admin_ok'),
          });
        }
      }
    });
  };

  const handleUploadImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    setLoading(true);
    const res = await adminUploadServiceImage(formData);
    setLoading(false);
    if (res.success && (res.data?.url || res.data?.path)) {
      const uploadedUrl = res.data.url || res.data.path || '';
      Swal.fire({
        title: t('admin_upload_success'),
        icon: 'success',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
        timer: 1500,
      });
      setEditForm((prev) => ({ ...prev, image_url: uploadedUrl }));
    } else {
      Swal.fire({
        title: t('admin_upload_error'),
        text: res.message || '',
        icon: 'error',
        confirmButtonColor: '#3DBFA0',
        confirmButtonText: t('admin_ok'),
      });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelected}
      />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-brand-dark font-heading">
            {t('admin_services_manager')}
          </h2>
          <p className="text-sm text-brand-muted mt-1">{t('admin_manage_services')}</p>
        </div>
        <button
          onClick={handleAdd}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-sm"
        >
          <i className="ri-add-line" />
          {t('admin_add_service_btn')}
        </button>
      </div>

      {/* Default content banner */}
      {usingMock && !loading && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 shrink-0">
            <i className="ri-information-line text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">
              {fetchError ? 'Backend not reachable' : 'Default services loaded'}
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              {fetchError
                ? `Showing default services from the landing page. (${fetchError})`
                : 'These services are displayed on the public site but are not saved in the backend yet. Edit and save them here to enable full management.'}
            </p>
          </div>
          <button
            onClick={loadServices}
            className="shrink-0 px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1" />Retry
          </button>
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="py-12 text-center text-brand-muted text-sm">
          <i className="ri-loader-4-line animate-spin text-xl mr-2" />
          {t('admin_loading_data')}
        </div>
      ) : !loading && !usingMock && items.length === 0 ? (
        /* True empty state — backend reachable but no services yet */
        <div className="py-16 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-brand-light mx-auto mb-4">
            <i className="ri-brush-line text-brand-dark text-2xl" />
          </div>
          <h3 className="text-base font-semibold text-brand-dark mb-1">No services yet</h3>
          <p className="text-sm text-brand-muted mb-6">
            Load the 7 default services from the landing page, or add a new one manually.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button
              onClick={seedDefaultServices}
              disabled={seeding}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-dark text-white text-sm font-semibold rounded-lg hover:bg-brand-dark/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {seeding ? (
                <><i className="ri-loader-4-line animate-spin" />Loading services...</>
              ) : (
                <><i className="ri-download-cloud-line" />Load Default Services (7)</>
              )}
            </button>
            <button
              onClick={handleAdd}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              Add Service Manually
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((item, index) => (
            <div
              key={item.id}
              className={`bg-white rounded-xl border p-5 transition-colors ${normalizeActive(item.active) ? 'border-gray-100 hover:border-brand-mint/20' : 'border-gray-200 bg-gray-50/50'}`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${normalizeActive(item.active) ? 'bg-brand-light' : 'bg-gray-200'}`}
                >
                  <i
                    className={`${item.icon} ${normalizeActive(item.active) ? 'text-brand-dark' : 'text-gray-400'} text-xl`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-brand-dark font-heading">
                      {item.title}
                    </h3>
                    {!normalizeActive(item.active) && (
                      <span className="px-2 py-0.5 bg-gray-200 text-gray-500 text-[10px] font-bold rounded-full uppercase">
                        {t('admin_inactive')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-brand-muted mt-1 leading-relaxed line-clamp-2">
                    {item.short_description}
                  </p>
                  <p className="text-xs text-brand-muted mt-1">
                    {t('admin_slide_order')} #{item.sort_order}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleEdit(index)}
                    className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    aria-label={t('admin_edit')}
                  >
                    <i className="ri-edit-line text-brand-dark" />
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                    aria-label={t('admin_delete')}
                  >
                    <i className="ri-delete-bin-line text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Add Modal */}
      {editing !== null && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-brand-dark/40 backdrop-blur-sm"
            onClick={() => setEditing(null)}
          />
          <div className="relative bg-white rounded-2xl border border-gray-100 shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto animate-[fadeIn_0.2s_ease-out]">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-brand-dark font-heading">
                  {editing === -1 ? t('admin_add') : t('admin_edit')} {t('admin_service')}
                </h3>
                <p className="text-xs text-brand-muted">{t('admin_manage_services')}</p>
              </div>
              <button
                onClick={() => setEditing(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-brand-dark" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${normalizeActive(editForm.active) ? 'bg-green-100' : 'bg-gray-200'}`}
                  >
                    <i
                      className={`text-lg ${normalizeActive(editForm.active) ? 'ri-eye-line text-green-600' : 'ri-eye-off-line text-gray-500'}`}
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-brand-dark">
                      {normalizeActive(editForm.active) ? t('admin_active') : t('admin_inactive')}
                    </p>
                    <p className="text-xs text-brand-muted">
                      {normalizeActive(editForm.active) ? t('admin_visible') : t('admin_hidden')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() =>
                    setEditForm({
                      ...editForm,
                      active: normalizeActive(editForm.active) ? 0 : 1,
                    })
                  }
                  className={`relative w-12 h-7 rounded-full transition-colors duration-200 cursor-pointer ${normalizeActive(editForm.active) ? 'bg-brand-mint' : 'bg-gray-300'}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-transform duration-200 ${normalizeActive(editForm.active) ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-2">
                  {t('admin_icon')}
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {iconOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setEditForm({ ...editForm, icon: opt.value })}
                      className={`flex flex-col items-center gap-1 p-2.5 rounded-lg border transition-all cursor-pointer ${editForm.icon === opt.value ? 'border-brand-mint bg-brand-mint/10 text-brand-mint' : 'border-gray-200 hover:border-brand-mint/30 text-brand-muted hover:text-brand-dark'}`}
                    >
                      <i className={`${opt.value} text-xl`} />
                      <span className="text-[10px] font-medium">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

                <div>
                <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-2">
                  {t('admin_preview_image')}
                </label>
                <div className="relative rounded-xl overflow-hidden border border-gray-200">
                  {editForm.image_url ? (
                    <img
                      src={editForm.image_url}
                      alt={editForm.title}
                      className="w-full h-40 object-cover"
                    />
                  ) : (
                    <div className="w-full h-40 bg-gray-100 flex items-center justify-center">
                      <div className="text-center">
                        <i className="ri-image-line text-gray-300 text-4xl" />
                        <p className="text-xs text-gray-400 mt-2">{t('admin_no_image')}</p>
                      </div>
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-brand-dark/0 hover:bg-brand-dark/30 transition-colors">
                    <button
                      onClick={handleUploadImage}
                      className="opacity-0 hover:opacity-100 inline-flex items-center gap-2 px-4 py-2 bg-white text-brand-dark text-sm font-medium rounded-lg shadow-lg transition-opacity cursor-pointer"
                    >
                      <i className="ri-upload-cloud-2-line" />
                      {editForm.image_url ? t('admin_change_image') : t('admin_upload_image_title')}
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-1.5">
                    {t('admin_service_title')}
                  </label>
                  <input
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    placeholder={t('admin_enter_service_name')}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-1.5">
                    {t('admin_slide_order')}
                  </label>
                  <input
                    type="number"
                    min={1}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                    value={editForm.sort_order}
                    onChange={(e) =>
                      setEditForm({ ...editForm, sort_order: Number(e.target.value) })
                    }
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-1.5">
                  {t('admin_short_desc')}
                </label>
                <input
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint transition-colors"
                  value={editForm.short_description}
                  onChange={(e) =>
                    setEditForm({ ...editForm, short_description: e.target.value })
                  }
                  placeholder={t('admin_enter_short_desc')}
                />
                <p className="text-xs text-brand-muted mt-1">{t('admin_shown_cards')}</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-brand-dark uppercase tracking-wider mb-1.5">
                  {t('admin_full_desc')}
                </label>
                <textarea
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-brand-dark focus:outline-none focus:border-brand-mint focus:ring-1 focus:ring-brand-mint resize-none transition-colors"
                  rows={4}
                  value={editForm.full_description}
                  onChange={(e) =>
                    setEditForm({ ...editForm, full_description: e.target.value })
                  }
                  placeholder={t('admin_enter_full_desc')}
                />
                <p className="text-xs text-brand-muted mt-1">{t('admin_shown_detail')}</p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex items-center justify-between">
              <span className="text-xs text-brand-muted">{t('admin_save_to_backend')}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditing(null)}
                  className="px-4 py-2 bg-gray-100 text-brand-dark text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  {t('admin_cancel')}
                </button>
                <button
                  onClick={saveEdit}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-brand-mint text-white text-sm font-semibold rounded-lg hover:bg-brand-mint/90 transition-colors cursor-pointer shadow-sm disabled:opacity-60"
                >
                  {loading ? (
                    <i className="ri-loader-4-line animate-spin" />
                  ) : (
                    <i className="ri-save-line" />
                  )}
                  {t('admin_save_service')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}